<template>
  <div class="register">
    <div class="register-title">{{ registerTitle }}</div>

    <steps
      class="form-steps"
      :steps="['安全校验', '公司信息', '联系人信息', '完成']"
      :value="currentStep"
    />

    <div class="center">
      <!-- 安全校验 -->
      <template v-if="currentStep === 1">
        <safety-check
          :value="editUserInfo ? editUserInfo.userName : ''"
          :type="isEdit ? '3' : '1'"
          @success="safetyCheckSuccess"
        />
      </template>

      <!-- 公司信息 -->
      <template v-if="currentStep === 2">
        <company-form
          :value="editUserInfo || {}"
          :mode="editUserInfo ? 'edit' : 'add'"
          submit-text="下一步"
          @submit="submitCompany"
        />
      </template>

      <!-- 联系人信息 -->
      <template v-if="currentStep === 3">
        <el-form
          key="informationForm"
          ref="contactForm"
          class="information-form"
          label-width="auto"
          label-position="right"
          :model="contactForm"
          :rules="contactFormRules"
        >
          <el-form-item label="联系人" prop="contact">
            <el-input
              v-model.trim="contactForm.contact"
              maxlength="10"
              show-word-limit
              placeholder="请填写联系人的姓名"
            />
          </el-form-item>
          <el-form-item label="性别" prop="gender">
            <el-radio-group v-model="contactForm.gender">
              <el-radio :label="2">女</el-radio>
              <el-radio :label="1">男</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="职务" prop="duties">
            <el-input
              v-model.trim="contactForm.duties"
              maxlength="40"
              show-word-limit
              placeholder="请填写联系人的职务名称"
            />
          </el-form-item>
          <el-form-item label="手机号码" prop="phone">
            <el-input
              v-model.number="contactForm.phone"
              placeholder="请填写联系人的手机号码"
              maxlength="11"
            />
          </el-form-item>
          <el-form-item label="手机验证码" prop="code">
            <el-input
              v-model="contactForm.code"
              class="code-input"
              placeholder="请输入对应手机号收到的验证码"
            >
              <span
                slot="suffix"
                class="getCode-button"
                @click="getPhoneVerifyCode"
              >{{ isGetting ? `${ currentSecond }s` : '获取验证码' }}</span>
            </el-input>
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="contactForm.password"
              class="code-input"
              placeholder="请设置6位数以上的密码"
              type="password"
            />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model="contactForm.confirmPassword"
              class="code-input"
              placeholder="请再次输入密码"
              type="password"
            />
          </el-form-item>
          <el-form-item>
            <el-button
              class="nextStep-button"
              type="primary"
              @click="submitContact"
            >下一步</el-button>
          </el-form-item>
        </el-form>
      </template>

      <!-- 表单结果提示 -->
      <template v-if="currentStep === 4">
        <div v-if="showSuccess" class="form-result">
          <img class="result-icon" src="@/assets/images/icon-success.png">
          <div class="result-title">提交成功</div>
          <div class="result-desc">
            您提交的注册资料成功，请耐心等待平台审核，如需修改提交资料
            <br>请点击这里
            <a class="link" @click="returnEdit">返回修改</a>
          </div>
        </div>
        <div v-else-if="showError" class="form-result">
          <img class="result-icon" src="@/assets/images/icon-error.png">
          <div class="result-title">审核失败！</div>
          <div class="result-desc">
            {{ errorMessage }}，
            <br>请点击这里
            <a class="link" @click="reset">重新修改</a>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Steps from '@/components/Steps'
import SafetyCheck from '@/components/SafetyCheck'
import CompanyForm from '@/components/CompanyForm'

import { register, getPhoneCode } from '@/api'

import regExp from '@/regexp'

import countDown from '@/mixins/countDown'

const EDIT_USERINFO_KEY = 'edit_userInfo'

export default {
  components: {
    Steps,
    SafetyCheck,
    CompanyForm
  },

  mixins: [countDown],

  data() {
    return {
      currentStep: 1,

      editUserInfo: this.$route.query.isEdit
        ? JSON.parse(localStorage.getItem(EDIT_USERINFO_KEY))
        : '',

      /* 安全校验 */
      safetyCheck: {},

      /* 公司信息 */
      companyForm: {},

      /* 联系人信息 */
      contactForm: {
        contact: '',
        gender: '',
        duties: '',
        phone: '',
        code: '',
        password: '',
        confirmPassword: ''
      },
      contactFormRules: {
        contact: [
          {
            required: true,
            message: '请填写联系人的姓名',
            trigger: 'blur'
          }
        ],
        gender: [
          {
            required: true,
            message: '请选择性别',
            trigger: 'change'
          }
        ],
        duties: [
          {
            required: true,
            message: '请填写联系人的职务名称',
            trigger: 'blur'
          }
        ],
        phone: [
          {
            required: true,
            message: '请填写联系人手机号码',
            trigger: 'blur'
          },
          {
            type: 'number',
            message: '请填写正确的联系人手机号码',
            trigger: 'blur'
          }
        ],
        code: [
          {
            required: true,
            message: '请输入对应手机号收到的验证码',
            trigger: 'blur'
          }
        ],
        password: [
          {
            required: true,
            message: '请输入密码',
            trigger: 'blur'
          },
          {
            min: 6,
            message: '请输入6~20位的密码',
            trigger: ['blur', 'change']
          },
          {
            validator: (rule, value, callback) => {
              if (!regExp.password.test(value)) {
                return callback(
                  new Error(
                    '密码必须包含数字、字母、字符至少两种，且在6~20位之间'
                  )
                )
              }

              if (this.contactForm.confirmPassword) {
                this.$refs.contactForm.validateField('confirmPassword')
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        confirmPassword: [
          {
            required: true,
            message: '请再次输入密码',
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (value !== this.contactForm.password) {
                return callback(new Error('两次输入密码不一致'))
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ]
      },

      registerID: '', // 注册成功之后的id，用于返回修改操作

      showSuccess: false,
      showError: false,
      errorMessage: ''
    }
  },

  computed: {
    isEdit() {
      const { isEdit } = this.$route.query

      if (isEdit && this.editUserInfo) {
        return true
      }

      return false
    },

    registerTitle() {
      return this.isEdit ? '修改账号信息' : '注册账号'
    }
  },

  watch: {
    $route: function() {
      // 每次路由变化都进行data初始化，防止用户从修改模式直接跳转注册（同一个路由页面数据复用不更新问题）
      Object.assign(this.$data, this.$options.data())
    }
  },

  created() {
    if (this.editUserInfo) {
      const { id, contact, gender, resign, phone, password, noPassCause = '' } = this.editUserInfo

      this.registerID = id
      this.contactForm = {
        contact: contact,
        gender: gender,
        duties: resign,
        phone: +phone,
        code: '',
        password: password,
        confirmPassword: password
      }

      this.showError = true
      this.errorMessage = noPassCause
      this.currentStep = 4
    }
  },

  methods: {
    /*
     * 安全校验通过
     */
    safetyCheckSuccess(value) {
      this.safetyCheck = value

      this.nextStep()
    },

    /*
     * 下一步操作
     */
    nextStep() {
      this.currentStep++

      document.documentElement.scrollTop = 0
    },

    /*
     * 提交公司信息
     */
    submitCompany(value) {
      this.companyForm = value

      this.nextStep()
    },

    /*
     * 获取手机验证码
     */
    getPhoneVerifyCode() {
      if (this.isGetting) return

      this.$refs.contactForm.validateField('phone', async errMsg => {
        if (!errMsg) {
          try {
            await getPhoneCode({
              phone: this.contactForm.phone
            })

            this.isGetting = true
            this.countDown()

            this.$message.success('发送成功！')
          } catch (error) {
            this.isGetting = false

            this.$message.error(error.message)
          }
        }
      })
    },

    /*
     * 提交联系人信息
     */
    submitContact() {
      this.$refs.contactForm.validate(async valid => {
        if (valid) {
          const params = {
            userName: this.safetyCheck.email,
            emailCode: this.safetyCheck.code,

            companyName: this.companyForm.name,
            userCategory: this.companyForm.category,
            foundedTime: this.companyForm.foundedTime,
            taxesNum: this.companyForm.taxpayerNumber,
            legalRepresentatives: this.companyForm.representative,
            creditCode: this.companyForm.creditCode,
            businessScope: this.companyForm.scope,
            businessRegister: this.companyForm.registrationNumber,
            reallyCapital: this.companyForm.paidCapital,
            registerCapital: this.companyForm.registrationCapital,
            registerAddress: this.companyForm.address,
            briefing: this.companyForm.introduction,
            businessLicense: this.companyForm.license,
            accessory: this.companyForm.attachment,

            contact: this.contactForm.contact,
            gender: this.contactForm.gender,
            resign: this.contactForm.duties,
            phone: this.contactForm.phone,
            code: this.contactForm.code,
            password: this.contactForm.password

          }

          // 用于用户信息编辑
          if (this.registerID) {
            Object.assign(params, {
              id: this.registerID
            })
          }

          try {
            const res = await register(params)
            const data = res.data

            this.registerID = data.id

            // 覆盖现有的数据
            this.editUserInfo = data
            localStorage.removeItem(EDIT_USERINFO_KEY)

            clearTimeout(this.countDownTimer)
            this.isGetting = false
            this.showSuccess = true
            this.nextStep()
          } catch (error) {
            this.$message.error(error.message)
          }
        }
      })
    },

    /*
     * 返回修改
     */
    returnEdit() {
      this.currentStep = 2
    },

    /*
     * 重新注册
     */
    reset() {
      this.currentStep = 1
    }
  }
}
</script>

<style lang='scss' scoped>
.register {
  min-height: 100%;
  background-color: #fff;
  padding: 20px 0 0;
  .register-title {
    position: relative;
    font-size: 28px;
    color: #000;
    font-weight: bold;
    margin: 0 15%;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: -22px;
      width: 8px;
      height: 26px;
      transform: translateY(-50%);
      background-color: #417ef7;
    }
  }
  .form-steps {
    margin: 47px auto 0;
  }
  .center {
    display: table;
    margin: 0 auto 50px;
  }
  .company-form,
  .information-form {
    margin: 69px 0 0;
  }
  /deep/ .information-form {
    .el-form-item {
      &:last-child {
        margin-bottom: 0;
      }
    }
    .el-form-item__content {
      width: 519px;
      .el-input__inner {
        height: 42px;
        line-height: 42px;
      }
    }
    .nextStep-button {
      margin: 20px 0 0;
    }
  }
  /deep/ .code-input {
    > input {
      padding-right: 121px;
    }
    .el-input__suffix {
      right: 24px;
      display: flex;
      align-items: center;
    }
    .getCode-button {
      cursor: pointer;
      font-size: 16px;
      color: #417ef7;
    }
  }
  .nextStep-button {
    width: 100%;
    height: 56px;
    border-radius: 6px;
    font-size: 18px;
    border: 0;
  }
  .form-result {
    margin: 88px 0 0;
    text-align: center;
    .result-icon {
      width: 77px;
      height: 77px;
    }
    .result-title {
      font-size: 22px;
      margin: 29px 0 0;
      + .result-desc {
        margin: 52px 0 0;
      }
    }
    .result-desc {
      font-size: 16px;
      line-height: 24px;
      margin: 32px 0 0;
      .link {
        cursor: pointer;
        color: #417ef7;
        text-decoration: underline;
      }
    }
  }
}
</style>
